import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["value", "onClick", "onChange", "open", "name", "className", "style", "disabled", "onClear", "noClear", "defaultValue", "resetable", "jss", "error"];
import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

var BtnTime = function BtnTime(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("path", {
    d: "M10.5 0C4.71 0 0 4.71 0 10.5S4.71 21 10.5 21 21 16.29 21 10.5 16.29 0 10.5 0zm0 20C5.262 20 1 15.738 1 10.5S5.262 1 10.5 1 20 5.262 20 10.5 15.738 20 10.5 20z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10 5H9v7h7v-1h-6z"
  }));
};

BtnTime.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 21 21"
};
import createStyles from "../createStyles";
import Row from "../Layout/Row";
import Button from "../Button";
import TextField from "../TextField";
import PickerClearButton from "../basePicker/PickerClearButton";
import PickerInputButton from "../basePicker/PickerInputButton";
import Input from "../Input";
import PickerResetWrapper from "../basePicker/PickerResetWrapper";
import { timePickerInputStyles } from "./styles";
export var useStyles = createStyles(function (theme) {
  return _extends({}, timePickerInputStyles(theme));
});
export var deserializeTimeToObject = function deserializeTimeToObject(value) {
  if (!value) {
    return {
      hour: '',
      minute: '',
      ampm: ''
    };
  }

  var valueArray = value.split(':');
  var minValueArray = valueArray[1].trim().split(' ');
  return {
    hour: (valueArray[0] || '00').trim(),
    minute: (minValueArray[0] || '00').trim(),
    ampm: minValueArray[1] || 'am'
  };
};

var serializeTimeFromValue = function serializeTimeFromValue(e, _ref, name, paddedValue) {
  var hour = _ref.hour,
      minute = _ref.minute,
      ampm = _ref.ampm;
  var value = e.target.value;
  var usableValue = paddedValue || value;

  if (!value) {
    return '';
  }

  switch (name) {
    case 'hour':
      return "".concat(usableValue, " : ").concat(minute || '00', " ").concat(ampm || 'am');

    case 'minute':
      return "".concat(hour || '00', " : ").concat(usableValue || '', " ").concat(ampm || 'am');

    case 'ampm':
      return "".concat(hour || '00', " : ").concat(minute || '00', " ").concat(usableValue);

    default:
      return '';
  }
};

var jumpToNextInput = function jumpToNextInput(ref) {
  ref.current.select();
};

var TimePickerInput = /*#__PURE__*/React.forwardRef(function TimePickerInput(_ref2, ref) {
  var _cn;

  var value = _ref2.value,
      onClick = _ref2.onClick,
      onChange = _ref2.onChange,
      open = _ref2.open,
      _ref2$name = _ref2.name,
      name = _ref2$name === void 0 ? '' : _ref2$name,
      className = _ref2.className,
      style = _ref2.style,
      disabled = _ref2.disabled,
      onClear = _ref2.onClear,
      noClear = _ref2.noClear,
      defaultValue = _ref2.defaultValue,
      resetable = _ref2.resetable,
      jss = _ref2.jss,
      error = _ref2.error,
      props = _objectWithoutProperties(_ref2, _excluded);

  var classes = useStyles({
    jss: jss
  });
  var hourRef = useRef();
  var minuteRef = useRef();
  var ampmRef = useRef();
  useEffect(function () {
    if (open) {
      hourRef.current.focus();
    }
  }, [open]);
  var time = useMemo(function () {
    return deserializeTimeToObject(value);
  }, [value]);

  var handleInputFocus = function handleInputFocus(e) {
    e.target.select();
  };

  var handleInputBlur = function handleInputBlur(inputName) {
    return function (e) {
      var inputValue = e.target.value;
      var paddedValue = inputValue.length === 1 ? "0".concat(inputValue) : inputValue;

      if (paddedValue !== inputValue) {
        var newValue = serializeTimeFromValue(e, time, inputName, paddedValue);
        onChange(e, newValue);
      }
    };
  };

  var handleReset = function handleReset(e) {
    e.stopPropagation();
    onChange(e, defaultValue);
  };

  var dispatchEvent = function dispatchEvent(e, newValue) {
    e.stopPropagation();
    onChange(e, newValue);
  };

  var handleAMPMChange = function handleAMPMChange(e) {
    var newValue = serializeTimeFromValue(e, time, 'ampm');
    dispatchEvent(e, newValue);
  };

  var handleHoursChange = function handleHoursChange(e) {
    var inputValue = e.currentTarget.value;

    if (inputValue < 0 || inputValue > 12) {
      return;
    }

    var newValue = serializeTimeFromValue(e, time, 'hour');
    dispatchEvent(e, newValue);

    if (inputValue.length === 2) {
      jumpToNextInput(minuteRef);
    }
  };

  var handleMinutesChange = function handleMinutesChange(e) {
    var inputValue = e.target.value;

    if (inputValue < 0 || inputValue >= 60) {
      return;
    }

    var newValue = serializeTimeFromValue(e, time, 'minute');
    dispatchEvent(e, newValue);

    if (inputValue.length === 2) {
      jumpToNextInput(ampmRef);
    }
  };

  var hour = time.hour,
      minute = time.minute,
      ampm = time.ampm;
  return /*#__PURE__*/React.createElement(Button, _extends({}, props, {
    enableRipple: false,
    ref: ref,
    value: value,
    name: name,
    className: cn(classes.root, (_cn = {}, _defineProperty(_cn, classes.disabled, disabled), _defineProperty(_cn, classes.focus, open), _defineProperty(_cn, classes.error, error), _cn), className),
    onClick: onClick,
    disabled: disabled,
    style: style
  }), /*#__PURE__*/React.createElement(PickerResetWrapper, {
    align: "center",
    justify: "between",
    resetable: resetable,
    onReset: handleReset
  }, /*#__PURE__*/React.createElement(PickerInputButton, {
    svg: BtnTime,
    disabled: disabled,
    active: open
  }), /*#__PURE__*/React.createElement(Row, {
    className: classes.row,
    span: "auto",
    align: "center"
  }, /*#__PURE__*/React.createElement(Input, {
    disabled: disabled,
    ref: hourRef,
    maxLength: 2,
    onFocus: handleInputFocus,
    onChange: handleHoursChange,
    onBlur: handleInputBlur('hour'),
    name: "hour",
    value: hour,
    className: classes.input,
    placeholder: "HH"
  }), /*#__PURE__*/React.createElement(TextField, {
    className: classes.separator
  }, ":"), /*#__PURE__*/React.createElement(Input, {
    disabled: disabled,
    maxLength: 2,
    ref: minuteRef,
    onFocus: handleInputFocus,
    onChange: handleMinutesChange,
    onBlur: handleInputBlur('minute'),
    name: "minute",
    value: minute,
    className: classes.input,
    placeholder: "MM"
  }), /*#__PURE__*/React.createElement(Input, {
    disabled: disabled,
    maxLength: 2,
    ref: ampmRef,
    onFocus: handleInputFocus,
    onChange: handleAMPMChange,
    name: "ampm",
    value: ampm,
    className: classes.input,
    placeholder: "AM"
  })), !noClear && !disabled && /*#__PURE__*/React.createElement(PickerClearButton, {
    show: !!value,
    onClick: onClear
  })));
});
TimePickerInput.propTypes = {
  /** Selected value to pass to the input */
  value: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array, PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  onClear: PropTypes.func,

  /** Prop to remove clear button if exists */
  noClear: PropTypes.bool,

  /** Disabled boolean indicator */
  disabled: PropTypes.bool,

  /** Additional className */
  className: PropTypes.string,

  /** Additional style */
  style: PropTypes.shape(),

  /** form level name */
  name: PropTypes.string,

  /** Indicator whether or not the dropdown is open */
  open: PropTypes.bool,

  /** jss override  */
  jss: PropTypes.oneOfType([PropTypes.shape(), PropTypes.func])
};
TimePickerInput.displayName = 'TimePickerInput';
export default TimePickerInput;